import type {
  ActionHistoryContext,
  ActionHistoryEntry,
  UserAction,
} from "@/types/actionHistory";
import { useCallback, useEffect, useState } from "react";

const ACTION_HISTORY_KEY = "user_action_history";
const MAX_HISTORY_ENTRIES = 100; // Limit to prevent localStorage from growing too large

// Custom event for action history updates
const ACTION_HISTORY_UPDATED_EVENT = "actionHistoryUpdated";

/**
 * Hook for tracking and retrieving user action history.
 * Actions are stored in localStorage with timestamps and context.
 */
export const useActionHistory = () => {
  /**
   * Retrieves the current action history from localStorage
   */
  const getActionHistory = useCallback((): ActionHistoryEntry[] => {
    try {
      const stored = localStorage.getItem(ACTION_HISTORY_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn("Failed to parse action history from localStorage:", error);
      return [];
    }
  }, []);

  /**
   * Gets the current action history (state-based for automatic re-rendering)
   */
  const [actionHistory, setActionHistory] = useState(() => getActionHistory());

  /**
   * Listen for action history updates from other hook instances
   */
  useEffect(() => {
    const handleActionHistoryUpdate = () => {
      setActionHistory(getActionHistory());
    };

    window.addEventListener(
      ACTION_HISTORY_UPDATED_EVENT,
      handleActionHistoryUpdate,
    );

    return () => {
      window.removeEventListener(
        ACTION_HISTORY_UPDATED_EVENT,
        handleActionHistoryUpdate,
      );
    };
  }, [getActionHistory]);

  /**
   * Dispatches a custom event to notify all hook instances of updates
   */
  const notifyActionHistoryUpdate = useCallback(() => {
    window.dispatchEvent(new CustomEvent(ACTION_HISTORY_UPDATED_EVENT));
  }, []);

  /**
   * Adds a new action to the history
   */
  const trackAction = useCallback(
    (action: UserAction, context: ActionHistoryContext) => {
      try {
        const currentHistory = getActionHistory();

        const newEntry: ActionHistoryEntry = {
          id: crypto.randomUUID(),
          action,
          timestamp: new Date().toISOString(),
          context,
        };

        // Add new entry to the beginning and limit total entries
        const updatedHistory = [newEntry, ...currentHistory].slice(
          0,
          MAX_HISTORY_ENTRIES,
        );

        localStorage.setItem(
          ACTION_HISTORY_KEY,
          JSON.stringify(updatedHistory),
        );
        setActionHistory(updatedHistory);

        // Notify all other hook instances of the update
        notifyActionHistoryUpdate();
      } catch (error) {
        console.warn("Failed to save action to history:", error);
      }
    },
    [getActionHistory, notifyActionHistoryUpdate],
  );

  /**
   * Clears all action history
   */
  const clearActionHistory = useCallback(() => {
    try {
      localStorage.removeItem(ACTION_HISTORY_KEY);
      setActionHistory([]);

      // Notify all other hook instances of the update
      notifyActionHistoryUpdate();
    } catch (error) {
      console.warn("Failed to clear action history:", error);
    }
  }, [notifyActionHistoryUpdate]);

  return {
    trackAction,
    actionHistory,
    clearActionHistory,
    getActionHistory,
  };
};
